/**
 * CurrencySelect.tsx — the FX increment-3 currency picker, shared by every
 * surface that writes a currency (expense create, new deal, invoice form,
 * settings default). Options are the server's CAPTURABLE set
 * (/v1/fx/currencies: GBP + quotes with a fresh fixing), so the picker can
 * never offer a code the write path would refuse — the OwnerSelect posture:
 * if the read fails it degrades to GBP-only, NEVER a free-text fallback
 * (free-text currency inputs are exactly what this picker retires). A
 * stored value outside the fetched set (e.g. a fixing gone stale since the
 * setting was saved) stays selectable so the form never silently rewrites
 * state; the server's named 400 remains the authority on submit.
 */
import { useAsync, fetchFxCurrencies } from './api'
import { currencySymbol } from '../../lib/money'

/** CurrencyGlyph (SB-9) — the currency-aware stand-in for the fixed
 *  PoundSterling icon beside money fields. Symboled majors render their
 *  character in the 12px icon slot; any OTHER code renders NOTHING — the
 *  adjacent label or money() render already names the code, and a
 *  three-letter code crammed into a glyph slot would be noise. Omitting
 *  beats showing a wrong or duplicate glyph. */
export function CurrencyGlyph({ code }: { code: string }) {
  const sym = currencySymbol(code)
  if (sym === code) return null
  return <span className="inline-block w-3 text-center text-12 font-semibold leading-none" aria-hidden="true">{sym}</span>
}

export function CurrencySelect({ value, onChange, className, id }: {
  value: string
  onChange: (currency: string) => void
  className: string
  id?: string
}) {
  const currencies = useAsync(() => fetchFxCurrencies(), [])
  const options = currencies.data && currencies.data.length > 0 ? currencies.data : ['GBP']
  const withCurrent = value && !options.includes(value) ? [...options, value] : options
  return (
    <select id={id} value={value} onChange={e => onChange(e.target.value)}
      className={className} disabled={currencies.loading}>
      {currencies.loading
        ? <option value={value || 'GBP'}>{value || 'GBP'}</option>
        : withCurrent.map(c => <option key={c} value={c}>{c}</option>)}
    </select>
  )
}
